import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";
import "../../../components/utils/PromangRichTextEditor.css";
import { EmployeeCustomSelector } from "../../../components/utils/moduleSelectors/employeeCustomSelector";
import { DocumentManagerV6 } from "../../../components/dashboard/Media/DocumentManagerV6";
import { updateMeetingMinutesEntry } from "../../../api/meetingMinutes";

function normalizeRichTextHtml(html) {
  if (!html) return "";
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return html;
}

function sameRich(left, right) {
  return normalizeRichTextHtml(left || "") === normalizeRichTextHtml(right || "");
}

export function MeetingEntryEditor({
  entry,
  groupId,
  meetingMinutesId,
  meetingId,
  onPatch,
  onPersisted,
  onRemove,
  onDocumentsChange,
  disabled = false,
  onSaveStatus,
  editVersion,
  onDraftChange,
  onFieldFocus,
  onAcceptRemote,
  freshEntriesRef,
  collabGeneration = 0,
  onRegisterFlush,
}) {
  const { t } = useTranslation();
  const [descDraft, setDescDraft] = useState(entry.description || "");
  const descDraftRef = useRef(entry.description || "");
  const descSaveTimer = useRef(null);
  const saveGeneration = useRef(0);
  const editVersionRef = useRef(editVersion);
  editVersionRef.current = editVersion;
  const fieldBaseRef = useRef({});
  const draftOriginRef = useRef({});
  const inFlightRef = useRef(new Map());
  const scalarDraftRef = useRef({
    deadline: entry.deadline || "",
    responsible_user_id: entry.responsible_user_id ?? null,
  });
  const flushPendingRef = useRef(async () => {});
  const persistedRef = useRef({
    description: normalizeRichTextHtml(entry.description || ""),
    deadline: entry.deadline || "",
    responsible_user_id: entry.responsible_user_id ?? null,
  });
  const descFocusedRef = useRef(false);
  const entryRef = useRef(entry);
  entryRef.current = entry;

  useEffect(() => {
    if (descSaveTimer.current) return;
    if (Object.prototype.hasOwnProperty.call(fieldBaseRef.current, "description")) return;
    if (descFocusedRef.current) return;
    const next = entry.description || "";
    if (next === descDraftRef.current) return;
    descDraftRef.current = next;
    setDescDraft(next);
    persistedRef.current.description = normalizeRichTextHtml(next);
  }, [entry.id, entry.description, entry.updated_at]);

  useEffect(() => {
    const entryId = entry.id;
    return () => {
      void flushPendingRef.current();
      ["description", "deadline", "responsible_user_id"].forEach((field) => {
        onFieldFocus?.(entryId, field, false);
      });
    };
  }, [entry.id, onFieldFocus]);

  const rememberBase = (field) => {
    if (!Object.prototype.hasOwnProperty.call(fieldBaseRef.current, field)) {
      fieldBaseRef.current[field] = editVersionRef.current;
      draftOriginRef.current[field] = editVersionRef.current;
    }
  };

  const clearFieldDraft = (field) => {
    delete fieldBaseRef.current[field];
    delete draftOriginRef.current[field];
  };

  const versionFor = (field) => (
    Object.prototype.hasOwnProperty.call(fieldBaseRef.current, field)
      ? fieldBaseRef.current[field] || editVersionRef.current
      : editVersionRef.current
  );

  const persistField = useCallback(
    async (field, value, { immediate = false } = {}) => {
      if (!entry?.id) return;

      const runSave = () => {
        if (descSaveTimer.current) {
          clearTimeout(descSaveTimer.current);
          descSaveTimer.current = null;
        }
        const generation = ++saveGeneration.current;
        const payload =
          field === "description"
            ? { description: normalizeRichTextHtml(descDraftRef.current) }
            : { [field]: value };
        const version = versionFor(field);
        if (version) payload.edit_version = version;

        const job = (async () => {
          try {
            onSaveStatus?.("saving");
            const updatedEntry = await updateMeetingMinutesEntry(
              meetingMinutesId,
              meetingId,
              groupId,
              entry.id,
              payload
            );

            if (!updatedEntry?.id) {
              const message =
                updatedEntry?.errors?.join?.(", ") ||
                updatedEntry?.response?.data?.errors?.join?.(", ") ||
                t("meeting_minutes.entry.update_error");
              throw new Error(message);
            }

            if (generation !== saveGeneration.current) return;

            onPersisted?.(groupId, entry.id, updatedEntry, {
              field,
              savedValue: payload[field],
            });

            const descriptionMoved = field === "description" && !sameRich(descDraftRef.current, payload.description);
            if (descriptionMoved) {
              const written = updatedEntry.edit_version || editVersionRef.current;
              fieldBaseRef.current.description = written;
              draftOriginRef.current.description = written;
              onDraftChange?.(entry.id, field, true);
              onSaveStatus?.("success");
              persistField("description", descDraftRef.current);
              return;
            }

            clearFieldDraft(field);
            if (field === "description") persistedRef.current.description = normalizeRichTextHtml(payload.description);
            if (field === "deadline") persistedRef.current.deadline = payload.deadline || "";
            if (field === "responsible_user_id") persistedRef.current.responsible_user_id = payload.responsible_user_id ?? null;
            onDraftChange?.(entry.id, field, false);
            onSaveStatus?.("success");
          } catch (err) {
            if (generation !== saveGeneration.current) return;
            console.error("Error updating entry:", err);
            const stale = err?.problem?.code === "STALE_MEETING";
            if (stale && Object.prototype.hasOwnProperty.call(draftOriginRef.current, field)) {
              fieldBaseRef.current[field] = draftOriginRef.current[field];
            }
            onSaveStatus?.(
              "error",
              stale ? err.problem.message || err.errors?.[0] : t("meeting_minutes.entry.update_error")
            );
          }
        })();

        inFlightRef.current.set(field, job);
        job.finally(() => {
          if (inFlightRef.current.get(field) === job) inFlightRef.current.delete(field);
        });
        return job;
      };

      if (immediate) return runSave();

      if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
      descSaveTimer.current = setTimeout(() => {
        descSaveTimer.current = null;
        runSave();
      }, 900);
    },
    [entry.id, groupId, meetingId, meetingMinutesId, onDraftChange, onPersisted, onSaveStatus, t]
  );

  const persistFieldRef = useRef(persistField);
  persistFieldRef.current = persistField;

  const flushPending = async () => {
    for (let pass = 0; pass < 3; pass += 1) {
      const started = [];
      const descriptionDirty = Object.prototype.hasOwnProperty.call(fieldBaseRef.current, "description");
      if (descSaveTimer.current || (pass === 0 && descriptionDirty && !inFlightRef.current.has("description"))) {
        started.push(persistFieldRef.current("description", descDraftRef.current, { immediate: true }));
      }
      ["deadline", "responsible_user_id"].forEach((field) => {
        if (pass !== 0) return;
        if (!Object.prototype.hasOwnProperty.call(fieldBaseRef.current, field)) return;
        if (inFlightRef.current.has(field)) return;
        started.push(persistFieldRef.current(field, scalarDraftRef.current[field], { immediate: true }));
      });
      const inflight = [...inFlightRef.current.values()];
      if (started.length === 0 && inflight.length === 0) return;
      await Promise.all([...started, ...inflight]);
    }
  };
  flushPendingRef.current = flushPending;

  useEffect(() => {
    if (!onRegisterFlush) return undefined;
    return onRegisterFlush(() => flushPendingRef.current());
  }, [entry.id, onRegisterFlush]);

  useEffect(() => {
    if (!collabGeneration) return;
    const remote = freshEntriesRef?.current?.get(entry.id);
    if (!remote) return;
    const nextVersion = editVersionRef.current;

    const descriptionDirty = Object.prototype.hasOwnProperty.call(fieldBaseRef.current, "description") || descSaveTimer.current;
    if (descriptionDirty) {
      const serverDesc = remote.description || "";
      if (sameRich(serverDesc, descDraftRef.current)) {
        clearFieldDraft("description");
        if (descSaveTimer.current) {
          clearTimeout(descSaveTimer.current);
          descSaveTimer.current = null;
        }
        persistedRef.current.description = normalizeRichTextHtml(serverDesc);
        onDraftChange?.(entry.id, "description", false);
      } else if (sameRich(serverDesc, persistedRef.current.description)) {
        const previous = fieldBaseRef.current.description;
        if (nextVersion && previous && nextVersion !== previous) {
          fieldBaseRef.current.description = nextVersion;
          persistFieldRef.current("description", descDraftRef.current, { immediate: true });
        }
      }
    }

    const sameScalar = (left, right) => String(left ?? "") === String(right ?? "");
    const reconcileScalar = (field, serverValue, localValue) => {
      if (!Object.prototype.hasOwnProperty.call(fieldBaseRef.current, field)) return;
      if (!sameScalar(serverValue, persistedRef.current[field])) return;
      if (sameScalar(localValue, serverValue)) {
        clearFieldDraft(field);
        onDraftChange?.(entry.id, field, false);
        return;
      }
      const previous = fieldBaseRef.current[field];
      if (nextVersion && previous && nextVersion !== previous) {
        fieldBaseRef.current[field] = nextVersion;
        persistFieldRef.current(field, localValue, { immediate: true });
      }
    };
    const latest = entryRef.current;
    reconcileScalar("deadline", remote.deadline || "", latest.deadline || "");
    reconcileScalar("responsible_user_id", remote.responsible_user_id ?? null, latest.responsible_user_id ?? null);
  }, [collabGeneration, entry.id, onDraftChange]);

  const blurCleanField = (field) => {
    if (field === "description") descFocusedRef.current = false;
    onFieldFocus?.(entry.id, field, false);
    if (Object.prototype.hasOwnProperty.call(fieldBaseRef.current, field)) return;
    if (field === "description" && descSaveTimer.current) return;
    const remote = freshEntriesRef?.current?.get(entry.id);
    if (!remote) return;
    if (field === "description") {
      const next = remote.description || "";
      if (next !== descDraftRef.current) {
        descDraftRef.current = next;
        setDescDraft(next);
      }
      persistedRef.current.description = normalizeRichTextHtml(next);
    }
    onAcceptRemote?.(entry.id, field);
  };

  const handleDescChange = useCallback(
    (html, _delta, source) => {
      if (source === "api" || source === "silent") return;
      descDraftRef.current = html;
      setDescDraft(html);
      const dirty = !sameRich(html, persistedRef.current.description);
      onDraftChange?.(entry.id, "description", dirty);
      onPatch?.(groupId, entry.id, { description: html });
      if (!dirty) {
        if (descSaveTimer.current) {
          clearTimeout(descSaveTimer.current);
          descSaveTimer.current = null;
        }
        clearFieldDraft("description");
        return;
      }
      rememberBase("description");
      persistField("description", html);
    },
    [entry.id, groupId, onDraftChange, onPatch, persistField]
  );

  const handleResponsibleChange = useCallback(
    (userId, userObject) => {
      const nextId = userId ?? null;
      scalarDraftRef.current.responsible_user_id = nextId;
      const dirty = nextId !== (persistedRef.current.responsible_user_id ?? null);
      onDraftChange?.(entry.id, "responsible_user_id", dirty);
      onPatch?.(groupId, entry.id, {
        responsible_user_id: userId,
        responsible_user: userObject,
      });
      if (!dirty) {
        clearFieldDraft("responsible_user_id");
        return;
      }
      rememberBase("responsible_user_id");
      persistField("responsible_user_id", userId, { immediate: true });
    },
    [entry.id, groupId, onDraftChange, onPatch, persistField]
  );

  const handleDeadlineChange = useCallback(
    (event) => {
      const value = event.target.value;
      scalarDraftRef.current.deadline = value || "";
      const dirty = (value || "") !== (persistedRef.current.deadline || "");
      onDraftChange?.(entry.id, "deadline", dirty);
      onPatch?.(groupId, entry.id, { deadline: value });
      if (!dirty) {
        clearFieldDraft("deadline");
        return;
      }
      rememberBase("deadline");
      persistField("deadline", value, { immediate: true });
    },
    [entry.id, groupId, onDraftChange, onPatch, persistField]
  );

  return (
    <div className="space-y-3">
      <div
        onFocusCapture={() => {
          descFocusedRef.current = true;
          onFieldFocus?.(entry.id, "description", true);
        }}
        onBlurCapture={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          blurCleanField("description");
        }}
      >
        <PromangRichTextEditor
          value={descDraft}
          onChange={handleDescChange}
          placeholder={t("meeting_minutes.entry.description")}
          size="sm"
          framed
          templatesEnabled={false}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
        <input
          type="date"
          value={entry.deadline || ""}
          onFocus={() => {
            onFieldFocus?.(entry.id, "deadline", true);
          }}
          onBlur={() => blurCleanField("deadline")}
          onChange={handleDeadlineChange}
          disabled={disabled}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20 disabled:bg-slate-100"
        />
        <EmployeeCustomSelector
          label={t("meeting_minutes.entry.responsible")}
          selected={
            entry.responsible_user_id
              ? entry.responsible_user || { id: entry.responsible_user_id }
              : null
          }
          onChange={handleResponsibleChange}
          disabled={disabled}
        />
      </div>
      {!disabled && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onRemove(groupId, entry.id)}
            className="inline-flex items-center rounded-md px-2 py-1 text-xs text-red-600 transition hover:bg-red-50 hover:text-red-800 sm:text-sm"
            aria-label={t("meeting_minutes.entry.remove")}
          >
            <XMarkIcon className="mr-1 h-4 w-4" />
            {t("meeting_minutes.entry.remove")}
          </button>
        </div>
      )}
      <div className="mt-3">
        <DocumentManagerV6
          module="meeting_minutes"
          moduleParam={`${meetingMinutesId}:group:${groupId}:entry:${entry.id}`}
          mode="dropzone"
          showUpload={!disabled}
          allowDelete={!disabled}
          allowEdit={!disabled}
          allowCopy={true}
          allowMove={true}
          disabled={disabled}
          autoLoad={true}
          onDocumentsChange={onDocumentsChange}
        />
      </div>
    </div>
  );
}
